import { Handle, Position } from '@xyflow/react';
import { DeploymentUnitOutlined } from '@ant-design/icons';
import { Tooltip, Badge } from 'antd';

/* ═══════════════════════════════════════════════════
   AgentNode — DAG 自定义 Agent 节点

   三种状态样式:
   - idle:    灰边 + 低透明度(0.45)
   - running: 脉冲发光 + 旋转彩色边框 (conic-gradient)
   - done:    绿色填充 + 对勾 Badge

   结构: Top Handle(target) → 节点主体 → Bottom Handle(source)
   hover 显示 Agent 描述 Tooltip
   ═══════════════════════════════════════════════════ */

const STATUS_STYLES = {
  idle: {
    border: '1.5px solid var(--text-muted)',
    opacity: 0.45,
    boxShadow: 'none',
    background: 'var(--surface-1)',
  },
  running: {
    border: '1.5px solid transparent',
    opacity: 1,
    boxShadow: '0 0 20px 4px rgba(232, 210, 138, 0.25)',
    background: 'var(--night-800)',
  },
  done: {
    border: '2px solid #22c55e',
    opacity: 1,
    boxShadow: '0 0 16px rgba(34, 197, 94, 0.2)',
    background: 'rgba(34, 197, 94, 0.08)',
  },
};

const STATUS_LABELS = {
  idle: { text: '待执行', color: 'var(--text-muted)' },
  running: { text: '执行中', color: 'var(--accent)' },
  done: { text: '已完成', color: '#22c55e' },
};

export default function AgentNode({ data, selected }) {
  const status = data?.status || 'idle';
  const style = STATUS_STYLES[status];
  const label = STATUS_LABELS[status];
  const isRunning = status === 'running';
  const isDone = status === 'done';

  const nodeContent = (
    <div
      style={{
        position: 'relative',
        width: 160,
        borderRadius: 14,
        padding: '12px 14px',
        transition: 'all 300ms var(--ease)',
        cursor: 'default',
        ...style,
        ...(selected ? { outline: '2px solid var(--accent)', outlineOffset: 2 } : {}),
      }}
    >
      {/* running: 旋转彩边层 */}
      {isRunning && (
        <div
          style={{
            position: 'absolute',
            inset: -2,
            borderRadius: 16,
            background: 'conic-gradient(from 0deg, var(--accent), #6366f1, #8b5cf6, var(--accent), transparent, var(--accent))',
            animation: 'ao-spin 2.5s linear infinite',
            zIndex: -1,
          }}
        />
      )}

      {/* 节点头部: 图标 + 名称 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDone
              ? 'rgba(34, 197, 94, 0.15)'
              : isRunning
                ? 'rgba(232, 210, 138, 0.1)'
                : 'rgba(255, 255, 255, 0.04)',
            fontSize: 16,
            flexShrink: 0,
            transition: 'background 300ms var(--ease)',
          }}
        >
          {data?.icon ? (
            <span style={{ fontSize: 18 }}>{data.icon}</span>
          ) : (
            <DeploymentUnitOutlined
              style={{
                fontSize: 16,
                color: isDone ? '#22c55e' : isRunning ? 'var(--accent)' : 'var(--text-muted)',
                transition: 'color 300ms var(--ease)',
              }}
            />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.3,
            }}
          >
            {data?.label || 'Agent 节点'}
          </div>
          <div
            style={{
              fontSize: 10,
              color: label.color,
              fontWeight: 500,
              marginTop: 2,
              letterSpacing: '0.03em',
              transition: 'color 300ms var(--ease)',
            }}
          >
            {label.text}
          </div>
        </div>

        {/* done: 绿色对勾 Badge */}
        {isDone && (
          <Badge
            count={
              <span style={{ fontSize: 10, color: '#fff' }}>✓</span>
            }
            style={{
              backgroundColor: '#22c55e',
              border: '2px solid var(--night-900)',
              width: 18,
              height: 18,
              lineHeight: '14px',
              padding: 0,
            }}
          />
        )}
      </div>

      {/* 描述信息 (如有) */}
      {data?.desc && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            lineHeight: 1.4,
            color: 'var(--text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {data.desc}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* 顶部 Handle — 接收连接 */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: 'var(--accent)',
          width: 8,
          height: 8,
          border: '2px solid var(--night-900)',
          borderRadius: 4,
        }}
      />

      {/* 节点主体 — hover 显示 Tooltip */}
      {data?.desc ? (
        <Tooltip
          title={
            <div style={{ maxWidth: 220 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{data?.label || 'Agent 节点'}</div>
              <div style={{ fontSize: 11, opacity: 0.8, lineHeight: 1.5 }}>{data.desc}</div>
            </div>
          }
          placement="top"
          overlayStyle={{
            '--antd-arrow-background-color': 'var(--night-800)',
          }}
          color="var(--night-800)"
        >
          {nodeContent}
        </Tooltip>
      ) : (
        nodeContent
      )}

      {/* 底部 Handle — 输出连接 */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: 'var(--accent)',
          width: 8,
          height: 8,
          border: '2px solid var(--night-900)',
          borderRadius: 4,
        }}
      />
    </>
  );
}

export const __nodeTypes = { agent: AgentNode };
